/**
 * Guards for values that arrive from the API.
 *
 * <p>The wire types say {@code percentile: number | null}, and the server does not always agree:
 * Jackson is configured to leave a null property out of the JSON entirely, so what reaches the
 * client is {@code undefined}, and a check written as {@code !== null} waves it straight through.
 * That is how "Better than undefined% of London" appeared in the comparison - not a formatting
 * slip, but a whole class of bug that any {@code === null} test in this codebase can reproduce.
 *
 * <p>So nothing tests a wire value against null directly. It goes through {@link num} or
 * {@link text}, which ask what the value <em>is</em> rather than what it is not: a finite number,
 * or a string with something in it. Everything else - null, undefined, NaN, Infinity, a blank
 * string - comes back as null, and the one null is what the interface then has a sentence for.
 */

/** A finite number, or null. NaN and Infinity are missing values, not numbers. */
export function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** A string with something in it, or null. */
export function text(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/** A real boolean, or null. Used where "not recorded" and "false" must not be confused. */
export function bool(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

/**
 * True when a value is worth putting on screen at all.
 *
 * <p>Deliberately strict about numbers: a score of 0 is present, a score of NaN is not.
 */
export function present(value: unknown): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }
  if (typeof value === 'string') {
    return value.trim() !== '';
  }
  return true;
}
